import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, CanMatchFn, Route, Router, RouterStateSnapshot, UrlSegment, UrlTree } from '@angular/router';
import { Observable, of, forkJoin, from } from 'rxjs';
import { map, catchError, timeout as rxTimeout } from 'rxjs/operators';

import { UtilService } from 'src/app/services/util/util.service';
import { MenuService } from 'src/app/services/menu/menu.service';
import { WorkflowService } from 'src/app/services/workflow/workflow.service';
import { PermissionService } from 'src/app/services/permission/permission.service';

const CHECK_TIMEOUT = 20000;

function promiseWithTimeout<T>(p: Promise<T>, ms: number, fallback: T): Promise<T> {
  return new Promise(resolve => {
    let done = false;
    p.then(v => { if (!done) { done = true; resolve(v); } })
      .catch(() => { if (!done) { done = true; resolve(fallback); } });
    setTimeout(() => { if (!done) { done = true; resolve(fallback); } }, ms);
  });
}

function runChecks(
  config: {
    basicOp?: string | string[],
    allowAdmin?: boolean,
    extraPermissions?: string[],
    checkCategoryManager?: boolean,
    mode?: 'any' | 'all',
    podCheck?: { podTitle: string, operation: string, redirect?: boolean, skipAudit?: boolean }
  },
  util: UtilService,
  menu: MenuService,
  workflowService: WorkflowService,
  permissionService: PermissionService
): Observable<{ admin: boolean, ops: boolean[], extras: boolean[], category: boolean }> {
  const { basicOp, allowAdmin, extraPermissions, checkCategoryManager, podCheck } = config;

  const adminCheck$ = allowAdmin
    ? from(promiseWithTimeout(menu.checkIsAdminstrator().then(() => !!menu.isAdministrator), CHECK_TIMEOUT, false))
    : of(false);

  const ops: string[] = basicOp ? (Array.isArray(basicOp) ? basicOp : [basicOp]) : [];
  const opChecks = ops.length
    ? ops.map(op => util.getBasicOperation(op).pipe(rxTimeout(CHECK_TIMEOUT), catchError(() => of(false))))
    : [of(false)];
  const opsFork$ = forkJoin(opChecks).pipe(catchError(() => of([] as boolean[])));

  const extraChecks = extraPermissions && extraPermissions.length
    ? extraPermissions.map(p => from(promiseWithTimeout(menu.hasPermission(p, false).then((r: any) => !!r?.manager), CHECK_TIMEOUT, false)))
    : [of(false)];
  const extrasFork$ = forkJoin(extraChecks).pipe(catchError(() => of([] as boolean[])));

  const categoryCheck$ = checkCategoryManager
    ? workflowService.isCategoryManager().pipe(rxTimeout(CHECK_TIMEOUT), catchError(() => of(false)))
    : of(false);

  if (podCheck?.podTitle) {
    const { podTitle, operation, redirect, skipAudit } = podCheck;
    menu.getPodWithOperation(podTitle, operation, redirect, skipAudit)
      .then((pod: any) => {
        if (pod?.pod) {
          permissionService.setPod(pod);
        }
      })
      .catch(() => {});
  }

  return forkJoin({
    admin: adminCheck$,
    ops: opsFork$,
    extras: extrasFork$,
    category: categoryCheck$
  });
}

function evaluateResults(res: any, config: { basicOp?: string | string[], mode?: 'any' | 'all' }): boolean {
  const ops: string[] = config.basicOp ? (Array.isArray(config.basicOp) ? config.basicOp : [config.basicOp]) : [];
  const mode = config.mode || 'any';

  if (res.admin) {
    return true;
  }

  const opResults: boolean[] = Array.isArray(res.ops) ? res.ops : [];
  const opsAllowed = ops.length > 0 && opResults.length > 0 ? (mode === 'all' ? opResults.every(Boolean) : opResults.some(Boolean)) : false;
  const extraResults: boolean[] = Array.isArray(res.extras) ? res.extras : [];
  const extrasAllowed = extraResults.some(Boolean);
  return opsAllowed || extrasAllowed || !!res.category;
}

function setManagerFlagsFromResults(res: any, config: { basicOp?: string | string[], extraPermissions?: string[] }, permissionService: PermissionService) {
  const ops: string[] = config.basicOp ? (Array.isArray(config.basicOp) ? config.basicOp : [config.basicOp]) : [];
  const opResults: boolean[] = Array.isArray(res.ops) ? res.ops : [];

  const flag = (opName: string) => {
    const idx = ops.indexOf(opName);
    return idx >= 0 ? !!opResults[idx] : false;
  };
  const extraResults: boolean[] = Array.isArray(res.extras) ? res.extras : [];
  let resetPwd = false;
  if (config.extraPermissions && config.extraPermissions.length) {
    const idx = config.extraPermissions.indexOf('MANAGER_USER_RESET_PASSWORD_CHANGE');
    if (idx >= 0 && extraResults.length > idx) {
      resetPwd = !!extraResults[idx];
    }
  } else {
    if (extraResults.length === 1) {
      resetPwd = !!extraResults[0];
    }
  }

  permissionService.setManagers({
    usersManager: flag('MANAGER_USER'),
    indirectUsersManager: flag('MANAGER_USER_INDIRECT'),
    indirectUsersGeneralManager: flag('MANAGER_USER_GENERAL_INDIRECT'),
    usersSearchManager: flag('MANAGER_USER_SEARCH'),
    resetPasswordPermission: resetPwd
  });
}

export const operationGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> => {
  const util = inject(UtilService);
  const menu = inject(MenuService);
  const router = inject(Router);
  const workflowService = inject(WorkflowService);
  const permissionService = inject(PermissionService);

  const basicOp = route.data?.['basicOperation'];
  const mode: 'any' | 'all' = route.data?.['mode'] ?? 'any';
  const redirectPath: string = route.data?.['redirectPath'] ?? '/accessDenied';
  const allowAdmin: boolean = !!route.data?.['allowAdmin'];
  const extraPermissions: string[] = route.data?.['extraPermissions'] ?? [];
  const checkCategoryManager: boolean = !!route.data?.['checkCategoryManager'];
  const podCheck = route.data?.['podCheck'];

  if (!basicOp && !allowAdmin && extraPermissions.length === 0 && !checkCategoryManager) {
    return of(router.createUrlTree([redirectPath], { queryParams: { url: state.url } }));
  }

  return runChecks({ basicOp, allowAdmin, extraPermissions, checkCategoryManager, mode, podCheck }, util, menu, workflowService, permissionService).pipe(
    map(res => {
      setManagerFlagsFromResults(res, { basicOp, extraPermissions }, permissionService);
      const allowed = evaluateResults(res, { basicOp, mode });
      if (allowed) return true;
      return router.createUrlTree([redirectPath], { queryParams: { url: state.url } });
    }),
    catchError(() => of(router.createUrlTree([redirectPath], { queryParams: { url: state.url } })))
  );
};

export const operationMatchGuard: CanMatchFn = (route: Route, segments: UrlSegment[]): Observable<boolean> => {
  const util = inject(UtilService);
  const menu = inject(MenuService);
  const router = inject(Router);
  const workflowService = inject(WorkflowService);
  const permissionService = inject(PermissionService);

  const data: any = route?.data ?? {};
  const basicOp = data['basicOperation'];
  const mode: 'any' | 'all' = data['mode'] ?? 'any';
  const redirectPath: string = data['redirectPath'] ?? '/accessDenied';
  const allowAdmin: boolean = !!data['allowAdmin'];
  const extraPermissions: string[] = data['extraPermissions'] ?? [];
  const checkCategoryManager: boolean = !!data['checkCategoryManager'];
  const podCheck = data['podCheck'];

  const attemptedUrl = segments?.length ? '/' + segments.map(s => s.path).join('/') : '/';

  if (!basicOp && !allowAdmin && extraPermissions.length === 0 && !checkCategoryManager) {
    router.navigate([redirectPath], { queryParams: { url: attemptedUrl } });
    return of(false);
  }

  return runChecks({ basicOp, allowAdmin, extraPermissions, checkCategoryManager, mode, podCheck }, util, menu, workflowService, permissionService).pipe(
    map(res => {
      setManagerFlagsFromResults(res, { basicOp, extraPermissions }, permissionService);
      const allowed = evaluateResults(res, { basicOp, mode });
      if (!allowed) {
        router.navigate([redirectPath], { queryParams: { url: attemptedUrl } });
      }
      return allowed;
    }),
    catchError(() => {
      router.navigate([redirectPath], { queryParams: { url: attemptedUrl } });
      return of(false);
    })
  );
};
